import React from "react";
import { useDialog } from "./useDialog";

/**
 * Painel de modal com acessibilidade embutida: role=dialog, aria-modal,
 * focus-trap, retorno de foco ao trigger e Esc (via useDialog).
 *
 * Substitui o `<div>` interno dos modais feitos à mão. Como só é montado
 * quando o overlay condicional (`{open && ...}`) renderiza, o useDialog
 * dispara na abertura — o que não acontece se o hook for chamado no topo
 * de um componente que fica montado com o modal fechado.
 *
 * O backdrop (div externo com position:fixed) e seu onClick continuam como
 * estão; troque só o painel interno por este e remova o <EscClose> (o Esc
 * agora vem daqui).
 */
export function DialogPanel({
  onClose,
  label,
  role = "dialog",
  style,
  children,
  ...rest
}: {
  onClose: () => void;
  label?: string;
  role?: "dialog" | "alertdialog";
  style?: React.CSSProperties;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "role" | "style">) {
  const ref = useDialog(onClose);
  return (
    <div
      ref={ref}
      role={role}
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      style={{ outline: "none", ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}
